"use client";

import React from "react";
import { usePathname } from "next/navigation";
import {
  identifyClarityUser,
  initClarity,
  setClarityConsent,
  setClarityTag,
} from "@lattice/frontend/lib/analytics/clarity";
import {
  identifyUser,
  initMixpanel,
  registerSuperProperties,
  resetUser,
  setUserProperties,
} from "@lattice/frontend/lib/analytics/mixpanel";
import {
  MixpanelEvents,
  trackEvent,
  trackPage,
} from "@lattice/frontend/lib/analytics/track";
import type { T_AnalyticsProperties } from "@lattice/frontend/lib/analytics/types";

function getBrowserPageProperties(): T_AnalyticsProperties {
  if (typeof window === "undefined") return {};

  return {
    url: window.location.href,
    search: window.location.search || null,
    title: document.title || null,
    referrer: document.referrer || null,
  };
}

type T_WebAnalyticsTrackerProps = {
  mixpanelApiUrl?: string;
  mixpanelMode?: string;
  mixpanelToken?: string;
  clarityProjectId?: string;
  appEnvironment: string;
  isClarityEnabled: boolean;
  clarityAnalyticsStorage: "granted" | "denied";
  userCuid?: string | null;
  userProperties?: T_AnalyticsProperties;
};

export function WebAnalyticsTracker({
  mixpanelApiUrl,
  mixpanelMode,
  mixpanelToken,
  clarityProjectId,
  appEnvironment,
  isClarityEnabled,
  clarityAnalyticsStorage,
  userCuid,
  userProperties,
}: T_WebAnalyticsTrackerProps) {
  const pathname = usePathname();
  const hasTrackedLaunchRef = React.useRef(false);
  const previousUserCuidRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    initMixpanel(
      mixpanelToken,
      {
        platform: "web",
        environment: appEnvironment,
      },
      {
        apiUrl: mixpanelApiUrl,
        mode: mixpanelMode,
      },
    );
  }, [appEnvironment, mixpanelApiUrl, mixpanelMode, mixpanelToken]);

  React.useEffect(() => {
    if (!isClarityEnabled) return;

    initClarity(clarityProjectId);
  }, [clarityProjectId, isClarityEnabled]);

  React.useEffect(() => {
    if (!isClarityEnabled) return;

    setClarityTag("platform", "web");
    setClarityTag("environment", appEnvironment);
  }, [appEnvironment, isClarityEnabled]);

  React.useEffect(() => {
    if (!isClarityEnabled) return;

    setClarityConsent({
      ad_Storage: "denied",
      analytics_Storage: clarityAnalyticsStorage,
    });
  }, [clarityAnalyticsStorage, isClarityEnabled]);

  React.useEffect(() => {
    registerSuperProperties({
      platform: "web",
      environment: appEnvironment,
    });
  }, [appEnvironment]);

  React.useEffect(() => {
    if (userCuid) {
      identifyUser(userCuid);
      previousUserCuidRef.current = userCuid;
      return;
    }

    if (previousUserCuidRef.current) {
      resetUser();
      registerSuperProperties({
        platform: "web",
        environment: appEnvironment,
      });
      previousUserCuidRef.current = null;
    }
  }, [appEnvironment, userCuid]);

  React.useEffect(() => {
    if (!isClarityEnabled || !userCuid) return;

    identifyClarityUser(userCuid, undefined, pathname);
  }, [isClarityEnabled, pathname, userCuid]);

  React.useEffect(() => {
    if (!userProperties) return;
    setUserProperties(userProperties);
    registerSuperProperties(userProperties);
  }, [userProperties]);

  React.useEffect(() => {
    if (isClarityEnabled) {
      setClarityTag("page_path", pathname);
    }
  }, [isClarityEnabled, pathname]);

  React.useEffect(() => {
    const pageProperties = getBrowserPageProperties();
    if (!hasTrackedLaunchRef.current) {
      trackEvent(MixpanelEvents.APP_LAUNCHED, {
        entry_path: pathname,
        ...pageProperties,
      });
      hasTrackedLaunchRef.current = true;
    }

    trackPage(pathname, pageProperties);
  }, [pathname]);

  return null;
}
